import React, { useEffect, useState } from 'react';
import { useToken } from '../App';
import { API_ENDPOINTS } from '../config/api';
import { Spinner, Modal, Button, InputGroup, FormControl, Badge } from 'react-bootstrap';
import { useHotelTitle } from '../hooks/useHotelTitle';

const GuestNearby: React.FC = () => {
  const { hotelId } = useToken()!;
  const { hotelName } = useHotelTitle('Nearby Places');
  const [options, setOptions] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [activeCat, setActiveCat] = useState<number|null>(null);
  const [selected, setSelected] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (!hotelId) return;
    setLoading(true);
    fetch(API_ENDPOINTS.NEARBY_OPTIONS(hotelId))
      .then(res => res.json())
      .then(data => {
        setOptions(Array.isArray(data) ? data : []);
        // Extract unique categories from options
        const cats: any[] = [];
        const seen = new Set();
        data.forEach((opt: any) => {
          if (opt.category && !seen.has(opt.category.nearby_cat_id)) {
            cats.push(opt.category);
            seen.add(opt.category.nearby_cat_id);
          }
        });
        setCategories(cats);
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load nearby options.');
        setLoading(false);
      });
  }, [hotelId]);

  const filtered = options.filter(opt => {
    const matchCat = activeCat ? opt.category?.nearby_cat_id === activeCat : true;
    const matchText = search.trim() ? (
      (opt.title && opt.title.toLowerCase().includes(search.toLowerCase())) ||
      (opt.address && opt.address.toLowerCase().includes(search.toLowerCase())) ||
      (opt.category && opt.category.nearby_cat_name.toLowerCase().includes(search.toLowerCase()))
    ) : true;
    return matchCat && matchText;
  });

  return (
    <div className="content" style={{ maxWidth: 600, margin: '0 auto', padding: 12, marginTop: 80 }}>
      <h2 className="section-title mb-2" style={{ fontSize: 19, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 22, color: '#4a6cf7' }}><i className="fas fa-location-dot"></i></span>
        Nearby Places
      </h2>
      <InputGroup className="mb-3 shadow-sm rounded">
        <InputGroup.Text><i className="fas fa-search"></i></InputGroup.Text>
        <FormControl
          placeholder="Search by name, address, or category..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </InputGroup>
      <div className="mb-3" style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}>
        <Button
          variant={activeCat === null ? 'primary' : 'outline-primary'}
          size="sm"
          className="me-2 mb-1"
          onClick={() => setActiveCat(null)}
        >
          <i className="fas fa-list"></i> All
        </Button>
        {categories.map((cat: any) => (
          <Button
            key={cat.nearby_cat_id}
            variant={activeCat === cat.nearby_cat_id ? 'primary' : 'outline-primary'}
            size="sm"
            className="me-2 mb-1"
            onClick={() => setActiveCat(cat.nearby_cat_id)}
          >
            <i className={cat.nearby_cat_icon}></i> {cat.nearby_cat_name}
          </Button>
        ))}
      </div>
      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" /></div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-muted py-5">
          <i className="fas fa-map-marker-alt fa-2x mb-2"></i>
          <div>No nearby places found.</div>
        </div>
      ) : (
        <div className="row g-2">
          {filtered.map(opt => (
            <div className="col-12" key={opt.nearby_option_id}>
              <div className="card shadow-sm border-0 mb-1 d-flex flex-row align-items-center" style={{ cursor: 'pointer', minHeight: 80 }} onClick={() => { setSelected(opt); setShowModal(true); }}>
                <div style={{ width: 64, height: 64, borderRadius: 12, overflow: 'hidden', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: 8 }}>
                  {opt.images && opt.images.length > 0 ? (
                    <img src={opt.primary_image} alt={opt.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span style={{ fontSize: 32, color: '#bbb' }}><i className={opt.category?.nearby_cat_icon || 'fas fa-map-marker-alt'}></i></span>
                  )}
                </div>
                <div className="flex-grow-1 px-2 py-2">
                  <div className="fw-bold" style={{ fontSize: 16 }}>{opt.title}</div>
                  <div className="text-muted small mb-1" style={{ fontSize: 13 }}>{opt.address}</div>
                  <div style={{ fontSize: 12 }}>
                    {opt.category && (
                      <Badge bg="secondary" className="me-1"><i className={opt.category.nearby_cat_icon}></i> {opt.category.nearby_cat_name}</Badge>
                    )}
                    {opt.distance_km && (
                      <Badge bg="info">{opt.distance_km} km</Badge>
                    )}
                  </div>
                </div>
                <div className="pe-3">
                  <Button variant="outline-primary" size="sm" onClick={e => { e.stopPropagation(); window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(opt.latitude + ',' + opt.longitude)}`, '_blank'); }}>
                    <i className="fas fa-location-arrow"></i>
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        {selected && (
          <>
            <Modal.Header closeButton>
              <Modal.Title>
                <i className={selected.category?.nearby_cat_icon}></i> {selected.title}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="mb-2">
                <strong>Address:</strong> {selected.address}
              </div>
              {selected.description && <div className="mb-2"><strong>Details:</strong> {selected.description}</div>}
              <div className="mb-2">
                <strong>Contact:</strong> {selected.contact || 'N/A'}
              </div>
              <div className="mb-2">
                <strong>Distance:</strong> {selected.distance_km ? selected.distance_km + ' km' : 'N/A'}
              </div>
              {selected.images && selected.images.length > 0 && (
                <div className="mb-2">
                  <div className="d-flex flex-row flex-nowrap overflow-auto" style={{ gap: 8 }}>
                    {selected.images.map((img: any, idx: number) => (
                      <img key={idx} src={img.image_path} alt="Nearby" style={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 8, border: '1px solid #eee' }} />
                    ))}
                  </div>
                </div>
              )}
              <Button variant="primary" className="mt-2" onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selected.latitude + ',' + selected.longitude)}`, '_blank')}>
                <i className="fas fa-location-arrow me-1"></i> Navigate
              </Button>
            </Modal.Body>
          </>
        )}
      </Modal>
    </div>
  );
};

export default GuestNearby; 